// ============================================================================
// DELIVERY MILESTONES
//
// Project-level phases. Stories and features are tagged to exactly one, so
// scope can be stated as "Feature A with stories B, C and D ships as Phase 1".
//
// Working content rather than settings, which is why it sits next to
// Requirements in the project nav — StakeholdersTab is the precedent for a
// light project-level list entity.
//
// Deleting a milestone that still has work tagged to it is refused first and
// only forced on confirmation: the tagged items are spread across sessions the
// user is not looking at, and silently untagging them would lose a phasing
// decision without anyone seeing it happen.
// ============================================================================

import React, { useState } from 'react';
import {
  Milestone as MilestoneIcon, Plus, Pencil, Trash2, Check, X, CalendarDays, AlertTriangle, Loader2,
} from 'lucide-react';
import { useMilestones, useMilestoneMutations } from '../../hooks/useMilestones';
import { Milestone, MilestoneStatus } from '../../types/raid';
import { MilestoneInUseError } from '../../services/milestones';
import { toast } from 'sonner';
import { MilestoneRoadmap } from './MilestoneRoadmap';

const STATUSES: { value: MilestoneStatus; label: string; chip: string }[] = [
  { value: 'planned', label: 'Planned', chip: 'bg-slate-100 text-slate-600 border-slate-200' },
  { value: 'in_progress', label: 'In progress', chip: 'bg-sky-50 text-sky-600 border-sky-200' },
  { value: 'delivered', label: 'Delivered', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'cancelled', label: 'Cancelled', chip: 'bg-slate-50 text-slate-400 border-slate-200' },
];

const statusConfig = (s: MilestoneStatus) => STATUSES.find(x => x.value === s) ?? STATUSES[0];

const INPUT = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const SELECT = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white';

interface DraftMilestone {
  milestoneId?: string;
  name: string;
  sequence: number;
  description: string;
  targetDate: string;
  status: MilestoneStatus;
}

export function MilestonesTab({ projectId, token = '' }: Readonly<{ projectId: string; token?: string }>) {
  const [mode, setMode] = useState<'list' | 'roadmap'>('list');
  const { data: milestones = [], isLoading } = useMilestones(projectId);
  const { create, update, remove } = useMilestoneMutations(projectId);

  const [draft, setDraft] = useState<DraftMilestone | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ milestone: Milestone; assigned: { stories: number; features: number } } | null>(null);

  const nextSequence = milestones.length > 0
    ? Math.max(...milestones.map(m => m.sequence)) + 10
    : 10;

  const startCreate = () => setDraft({
    name: '', sequence: nextSequence, description: '', targetDate: '', status: 'planned',
  });

  const startEdit = (m: Milestone) => setDraft({
    milestoneId: m.milestoneId,
    name: m.name,
    sequence: m.sequence,
    description: m.description ?? '',
    targetDate: m.targetDate ?? '',
    status: m.status,
  });

  const saveDraft = async () => {
    if (!draft?.name.trim()) return;
    const payload = {
      name: draft.name.trim(),
      sequence: draft.sequence,
      description: draft.description.trim() || undefined,
      targetDate: draft.targetDate || undefined,
      status: draft.status,
    };
    try {
      if (draft.milestoneId) {
        await update.mutateAsync({ milestoneId: draft.milestoneId, patch: payload });
      } else {
        await create.mutateAsync(payload);
      }
      setDraft(null);
    } catch {
      toast.error('Could not save the milestone');
    }
  };

  const attemptDelete = async (m: Milestone) => {
    try {
      await remove.mutateAsync({ milestoneId: m.milestoneId });
      toast.success(`Deleted ${m.name}`);
    } catch (error) {
      if (error instanceof MilestoneInUseError) {
        setConfirmDelete({ milestone: m, assigned: error.assigned });
        return;
      }
      toast.error('Could not delete the milestone');
    }
  };

  const forceDelete = async () => {
    if (!confirmDelete) return;
    try {
      const { untagged } = await remove.mutateAsync({ milestoneId: confirmDelete.milestone.milestoneId, force: true });
      toast.success(`Deleted ${confirmDelete.milestone.name} and untagged ${untagged} item${untagged === 1 ? '' : 's'}`);
    } catch {
      toast.error('Could not delete the milestone');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <MilestoneIcon size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Delivery Milestones</h1>
              <p className="text-slate-500 text-sm font-medium">
                Phases that user stories and features are delivered in. Project-level, so they persist across sessions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['list', 'roadmap'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-4 py-2 text-xs font-semibold capitalize transition-all ${
                    mode === m ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
            <button
              onClick={startCreate}
              disabled={!!draft || mode === 'roadmap'}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              <Plus size={16} /> Add milestone
            </button>
          </div>
        </header>

        {mode === 'roadmap' && <MilestoneRoadmap projectId={projectId} token={token} />}

        {mode === 'list' && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          {isLoading && (
            <div className="flex justify-center items-center py-20 text-[#64748b] text-sm">Loading milestones…</div>
          )}

          {!isLoading && milestones.length === 0 && !draft && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <MilestoneIcon size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No milestones yet.</p>
              <p className="text-slate-400 text-xs mt-2">
                Add one, then tag stories and features to it from the requirements view.
              </p>
            </div>
          )}

          {(milestones.length > 0 || draft) && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5 w-20">Seq</th>
                  <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5">Milestone</th>
                  <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5 w-36">Target</th>
                  <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5 w-32">Status</th>
                  <th className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5 w-24">Scope</th>
                  <th className="w-20 px-2 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {milestones.map(m => (
                  draft?.milestoneId === m.milestoneId ? (
                    <DraftRow key={m.milestoneId} draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={() => setDraft(null)} saving={update.isPending} />
                  ) : (
                    <tr key={m.milestoneId} className="group hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono text-slate-400">{m.sequence}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-navy">{m.name}</p>
                        {m.description && <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {m.targetDate ? (
                          <span className="flex items-center gap-1"><CalendarDays size={11} />{m.targetDate}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusConfig(m.status).chip}`}>
                          {statusConfig(m.status).label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {m.assignedCount ? `${m.assignedCount} item${m.assignedCount === 1 ? '' : 's'}` : <span className="text-slate-300">Empty</span>}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => startEdit(m)} aria-label={`Edit ${m.name}`}
                            className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => attemptDelete(m)} aria-label={`Delete ${m.name}`}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}
                {draft && !draft.milestoneId && (
                  <DraftRow draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={() => setDraft(null)} saving={create.isPending} />
                )}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5 flex gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-navy">Delete “{confirmDelete.milestone.name}”?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {confirmDelete.assigned.stories} user stor{confirmDelete.assigned.stories === 1 ? 'y' : 'ies'} and{' '}
                  {confirmDelete.assigned.features} feature{confirmDelete.assigned.features === 1 ? '' : 's'} are still
                  tagged to this phase, across every session in the project.
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Deleting it will untag them. They are not deleted, but the phasing decision is lost.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
              <button onClick={() => setConfirmDelete(null)}
                className="rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={forceDelete} disabled={remove.isPending}
                className="flex items-center gap-2 bg-red-600 text-white font-bold text-sm h-10 px-4 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-60">
                {remove.isPending && <Loader2 size={14} className="animate-spin" />}
                Untag and delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DraftRowProps {
  draft: DraftMilestone;
  setDraft: (d: DraftMilestone) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}

function DraftRow({ draft, setDraft, onSave, onCancel, saving }: Readonly<DraftRowProps>) {
  return (
    <tr className="bg-primary/[0.02]">
      <td className="px-4 py-3">
        <input
          type="number"
          value={draft.sequence}
          onChange={e => setDraft({ ...draft, sequence: Number(e.target.value) })}
          aria-label="Sequence"
          className={`${INPUT} px-2 py-1.5 text-xs`}
        />
      </td>
      <td className="px-4 py-3">
        <input
          value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          placeholder="Phase 1"
          aria-label="Milestone name"
          autoFocus
          className={`${INPUT} py-1.5`}
        />
        <input
          value={draft.description}
          onChange={e => setDraft({ ...draft, description: e.target.value })}
          placeholder="What this phase covers"
          aria-label="Description"
          className={`${INPUT} py-1.5 mt-1.5 text-xs`}
        />
      </td>
      <td className="px-4 py-3">
        <input
          type="date"
          value={draft.targetDate}
          onChange={e => setDraft({ ...draft, targetDate: e.target.value })}
          aria-label="Target date"
          className={`${SELECT} px-2 py-1.5 text-xs`}
        />
      </td>
      <td className="px-4 py-3">
        <select
          value={draft.status}
          onChange={e => setDraft({ ...draft, status: e.target.value as MilestoneStatus })}
          aria-label="Status"
          className={`${SELECT} px-2 py-1.5 text-xs`}
        >
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td className="px-4 py-3 text-xs text-slate-300">—</td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-1">
          <button onClick={onSave} disabled={!draft.name.trim() || saving} aria-label="Save milestone"
            className="p-1 rounded hover:bg-primary/10 text-primary disabled:opacity-40 transition-colors">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
          </button>
          <button onClick={onCancel} aria-label="Cancel"
            className="p-1 rounded hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
}
